/** @file place: every visible impostor pick of a pose written into an ImpostorList (design 5 step 3, R2, R3). */
import type { ImpostorDrawable } from '@zilo/pose3d-format/types/drawable';
import { POSE_FLAGS } from '@zilo/pose3d-pose/layout/poseFlags';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import { nodeIndexOf } from '@zilo/pose3d-render-core/camera/nodeIndexOf';
import { viewOf } from '@zilo/pose3d-render-core/camera/viewOf';
import { RENDER_CORE_ERRORS } from '@zilo/pose3d-render-core/errors/renderCoreErrors';
import { IMPOSTOR_LAYOUT as L } from '@zilo/pose3d-render-core/layout/impostorLayout';
import type { HostedPlace } from '@zilo/pose3d-render-core/place/hostedPlace';
import { impostorFieldsOf } from '@zilo/pose3d-render-core/place/impostorFieldsOf';
import { partMapOf } from '@zilo/pose3d-render-core/place/partMapOf';
import { pickByDrawable } from '@zilo/pose3d-render-core/place/pickByDrawable';
import { poseNodeOf } from '@zilo/pose3d-render-core/place/poseNodeOf';
import { writeImpostor } from '@zilo/pose3d-render-core/place/writeImpostor';

/**
 * Writes the impostors of `puppet.drawables` in order, one stride each, and returns the count. Skipped: non-impostor
 * drawables, drawables without a pick, picks `hidden` or `missing`, nodes with POSE flag `hidden`. Throws
 * `IMPOSTOR_LIST_TOO_SMALL` (before writing) when `out` cannot hold every impostor drawable, `NODE_UNKNOWN`,
 * `SHEET_UNKNOWN`. Rounding, rotation and second points follow design 21.3-21.7 on the per-axis grid of 22.1; the
 * optional `host` (the host's `PoseParams`) moves each drawable point by its node's `partTransform` before rounding (22.3). A
 * `Place` (the frozen signature) as well as a `HostedPlace`.
 */
export const place: HostedPlace = (puppet, pose, picks, camera, profile, entityPos, out, host) => {
  const impostors = puppet.drawables.filter((d): d is ImpostorDrawable => d.kind === 'impostor').length;
  if (out.length < impostors * L.stride) {
    return throwError(RENDER_CORE_ERRORS.IMPOSTOR_LIST_TOO_SMALL, { length: out.length, needed: impostors * L.stride });
  }
  const view = viewOf(camera, profile);
  const byDrawable = pickByDrawable(picks);
  const nodeIndices = nodeIndexOf(puppet);
  const zScaleRotate = profile.zScaleRotate ?? 1;
  let count = 0;
  puppet.drawables.forEach((drawable, drawableIndex) => {
    if (drawable.kind !== 'impostor') return;
    const pick = byDrawable.get(drawable.id);
    if (pick === undefined || pick.hidden || pick.missing) return;
    const nodeIndex = nodeIndices.get(drawable.node) ?? throwError(RENDER_CORE_ERRORS.NODE_UNKNOWN, { node: drawable.node });
    if ((poseNodeOf(pose, nodeIndex).flags & POSE_FLAGS.hidden) !== 0) return;
    const parts = partMapOf(host, drawable.node, profile);
    writeImpostor(out, count++, impostorFieldsOf({
      puppet, pose, view, profile, cameraYawDeg: camera.yawDeg, entityPos, nodeIndex, drawable, drawableIndex, pick, nodeIndices, zScaleRotate,
      ...(parts === null ? {} : { parts }),
    }));
  });
  return count;
};
